const DEFAULT_MINIMUM_SCALE = 1.2;
const DEFAULT_DEBOUNCE_MS = 120;

export function highResolutionPageIndex(
  activeIndex: number,
  pageCount: number,
): number | null {
  if (pageCount <= 0) return null;
  return Math.min(Math.max(Math.trunc(activeIndex), 0), pageCount - 1);
}

interface HighResolutionPageOptions {
  images: readonly HTMLImageElement[];
  minimumScale?: number;
  debounceMs?: number;
}

export class HighResolutionPageController {
  private readonly images: readonly HTMLImageElement[];
  private readonly minimumScale: number;
  private readonly debounceMs: number;
  private targetIndex: number | null = null;
  private queuedIndex: number | null = null;
  private highResolutionIndex: number | null = null;
  private timer: number | undefined;
  private loading = false;
  private destroyed = false;

  constructor(options: HighResolutionPageOptions) {
    this.images = options.images;
    this.minimumScale = options.minimumScale ?? DEFAULT_MINIMUM_SCALE;
    this.debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  }

  update(activeIndex: number, scale: number): void {
    if (this.destroyed) return;
    window.clearTimeout(this.timer);

    if (scale < this.minimumScale) {
      this.targetIndex = null;
      this.queuedIndex = null;
      this.restoreAllResponsiveImages();
      return;
    }

    this.targetIndex = highResolutionPageIndex(activeIndex, this.images.length);
    this.timer = window.setTimeout(() => {
      this.queuedIndex = this.targetIndex;
      void this.processQueue();
    }, this.debounceMs);
  }

  destroy(): void {
    this.destroyed = true;
    this.targetIndex = null;
    this.queuedIndex = null;
    window.clearTimeout(this.timer);
  }

  private async processQueue(): Promise<void> {
    if (this.loading || this.destroyed) return;
    this.loading = true;

    while (!this.destroyed && this.queuedIndex !== null) {
      const index = this.queuedIndex;
      this.queuedIndex = null;
      if (index === this.highResolutionIndex) continue;
      await this.loadHighResolutionImage(index);
    }

    this.loading = false;
  }

  private async loadHighResolutionImage(index: number): Promise<void> {
    const image = this.images[index];
    const highSource = image?.dataset.highSrc;
    if (!image || !highSource) return;

    image.dataset.highResolution = "loading";
    const preloader = new Image();
    preloader.decoding = "async";
    preloader.fetchPriority = "high";
    preloader.src = highSource;

    try {
      await preloader.decode();
      if (this.destroyed || this.targetIndex !== index) {
        if (image.dataset.highResolution === "loading") {
          image.dataset.highResolution = "responsive";
        }
        return;
      }

      image.srcset = "";
      image.removeAttribute("sizes");
      image.src = highSource;
      await image.decode();
      if (this.destroyed || this.targetIndex !== index) {
        this.restoreResponsiveImage(image);
        return;
      }

      image.dataset.highResolution = "ready";
      this.restoreOtherResponsiveImages(index);
      this.highResolutionIndex = index;
    } catch (error) {
      if (this.targetIndex === index) {
        this.restoreResponsiveImage(image);
        image.dataset.highResolution = "error";
        console.error(`Unable to load high-resolution catalog page ${index + 1}.`, error);
      }
    }
  }

  private restoreOtherResponsiveImages(activeIndex: number): void {
    this.images.forEach((image, index) => {
      if (index !== activeIndex) this.restoreResponsiveImage(image);
    });
  }

  private restoreAllResponsiveImages(): void {
    this.images.forEach((image) => this.restoreResponsiveImage(image));
    this.highResolutionIndex = null;
  }

  private restoreResponsiveImage(image: HTMLImageElement): void {
    if (image.dataset.highResolution === "responsive") return;
    image.src = image.dataset.src ?? image.src;
    image.srcset = image.dataset.srcset ?? "";
    image.sizes = "100vw";
    image.fetchPriority = "auto";
    image.dataset.highResolution = "responsive";
    if (this.images[this.highResolutionIndex ?? -1] === image) {
      this.highResolutionIndex = null;
    }
  }
}
